import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useTypeOptions } from '@/components/TaxInformation/hooks';
import Input from '@/components/form/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/form/Select';
import Toggle from '@/components/form/Toggle';
import { useCountryOptions } from '@/hooks/location';
import { TAX_PAYER_TYPE } from '@/store/profile';
import { requiresTaxId, requiresVat } from '@/utils/billing';

import NoVatModal from './NoVatModal';

function TaxInformationFields({ typeValue, countryValue }) {
	const { t } = useTranslation();

	const typeOptions = useTypeOptions();
	const countries = useCountryOptions();
	const [noVatModal, setNoVatModal] = useState(false);

	return (
		<div className="m-0 flex w-full flex-col items-center">
			<div className="flex w-full flex-col gap-x-5 gap-y-4 lg:gap-y-6">
				<Toggle
					label={t('account.form.type')}
					name="type"
					options={typeOptions}
				/>

				{typeValue === TAX_PAYER_TYPE.COMPANY && (
					<Input
						label={t('account.form.company')}
						required
						name="companyName"
						placeholder={t('account.form.placeholder.company')}
						size="lg"
					/>
				)}

				<Select
					name="country"
					required
					label={`${t('account.form.country')}`}
				>
					<SelectTrigger
						size="lg"
						className="rounded"
						aria-label={t('account.form.country')}
					>
						<SelectValue
							placeholder={`${t('account.form.placeholder.country')}`}
						/>
					</SelectTrigger>
					<SelectContent className="border-0 border-grey-200 bg-white">
						{countries.map((res, index) => {
							return (
								<SelectItem key={index} value={res.value}>
									{res.label}
								</SelectItem>
							);
						})}
					</SelectContent>
				</Select>

				{requiresVat({
					country: countryValue,
					type: typeValue
				}) ? (
					<div>
						<Input
							label={t('account.form.vatNumber')}
							name="vatNumber"
							required
							placeholder={`${t('account.form.placeholder.vatNumber')}`}
							size="lg"
						/>
						<NoVatModal open={noVatModal} onOpenChange={setNoVatModal} />
					</div>
				) : (
					<Input
						label={
							countryValue === 'es' ? 'NIF/CIF' : t('account.form.taxId')
						}
						name="taxId"
						required={requiresTaxId(countryValue)}
						placeholder={`${t('account.form.placeholder.taxId')}`}
						size="lg"
					/>
				)}

				<div className="flex items-end gap-4">
					<Input
						label={t('account.form.name')}
						required
						name="firstName"
						placeholder={`${t('account.form.placeholder.name')}`}
						size="lg"
					/>

					<Input
						label={t('account.form.lastname')}
						required
						name="lastName"
						placeholder={`${t('account.form.placeholder.lastname')}`}
						size="lg"
					/>
				</div>

				<Input
					label={t('account.form.address')}
					required
					name="address"
					placeholder={`${t('account.form.placeholder.address')}`}
					size="lg"
				/>
				<div className="flex gap-4">
					<Input
						label={t('account.form.postal')}
						required
						name="zipCode"
						placeholder={`${t('account.form.placeholder.postal')}`}
						size="lg"
					/>

					<Input
						label={t('account.form.city')}
						required
						name="city"
						placeholder={`${t('account.form.placeholder.city')}`}
						size="lg"
					/>
				</div>

				<Input
					label={t('common.email')}
					required
					name="email"
					placeholder={`${t('account.form.placeholder.email')}`}
					size="lg"
				/>
			</div>
		</div>
	);
}

export default TaxInformationFields;
